/**
 * 替代品规则类型定义
 */

import type { PageRequest } from '../common';
import { SubstituteRuleStatus, SubstituteRuleType } from '../../constants/promotionRule';

// ============ 枚举定义 ============

// 匹配类型枚举
export enum MatchType {
  PRICE = 'PRICE',           // 价格
  CATEGORY = 'CATEGORY',     // 品类
  BRAND = 'BRAND',           // 品牌
}

// 匹配类型选项
export const MatchTypeOptions = [
  { label: 'promotionRule.form.matchType.price', value: MatchType.PRICE },
  { label: 'promotionRule.form.matchType.category', value: MatchType.CATEGORY },
  { label: 'promotionRule.form.matchType.brand', value: MatchType.BRAND },
];

// 品类层级枚举
export enum CategoryLevel {
  L1 = 'L1',   // 一级品类
  L2 = 'L2',   // 二级品类
  L3 = 'L3',   // 三级品类
}

// 品类层级选项
export const CategoryLevelOptions = [
  { label: 'promotionRule.form.categoryLevel.l1', value: CategoryLevel.L1 },
  { label: 'promotionRule.form.categoryLevel.l2', value: CategoryLevel.L2 },
  { label: 'promotionRule.form.categoryLevel.l3', value: CategoryLevel.L3 },
];

// 匹配规则枚举（根据匹配类型不同）
export enum MatchRule {
  MUST_MATCH = 'MUST_MATCH',       // 必须满足
  GREATER_THAN = 'GREATER_THAN',   // 大于
  LESS_THAN = 'LESS_THAN',         // 小于
  EQUAL = 'EQUAL',                 // 等于
  GREATER_THAN_OR_EQUAL = 'GREATER_THAN_OR_EQUAL', // 大于等于
  LESS_THAN_OR_EQUAL = 'LESS_THAN_OR_EQUAL',       // 小于等于
}

// 匹配规则选项
export const MatchRuleOptions = [
  { label: 'promotionRule.form.matchRule.mustMatch', value: MatchRule.MUST_MATCH },
  { label: 'promotionRule.form.matchRule.greaterThan', value: MatchRule.GREATER_THAN },
  { label: 'promotionRule.form.matchRule.lessThan', value: MatchRule.LESS_THAN },
  { label: 'promotionRule.form.matchRule.equal', value: MatchRule.EQUAL },
  { label: 'promotionRule.form.matchRule.greaterThanOrEqual', value: MatchRule.GREATER_THAN_OR_EQUAL },
  { label: 'promotionRule.form.matchRule.lessThanOrEqual', value: MatchRule.LESS_THAN_OR_EQUAL },
];

// ============ 基础类型 ============

// 基础字段
interface BaseFields {
  id: number;
  code: string;
  creatorAccount: string;
  creatorName: string;
  gmtCreate: number;
  modifierAccount: string;
  modifierName: string;
  gmtModified: number;
}

// 替代品匹配配置 DTO
export interface SubstituteMatchConfigDTO {
  matchType: MatchType;               // 匹配类型
  matchRule: MatchRule;               // 匹配规则
  priority: string | null;            // 优先级 (L1, L2, L3...)
  categoryLevel?: CategoryLevel;      // 品类层级（仅当 matchType 为 CATEGORY 时使用）
}

// 替代品明细 DTO
export interface SubstituteDetailDTO {
  substituteSku: string;             // 替换商品SKU
  substituteItemName: string;        // 替换商品名称（冗余）
  substitutePrice: number;           // 替换商品价格
  priceDifferenceAllowed: boolean;   // 是否允许价差
  maxPriceDifference: number | null; // 最大允许价差
  priceDiff: number;                 // 价格差异（自动计算：substitutePrice - originalPrice）
  priority: number;                  // 优先级（多个替换商品时，数字越小优先级越高）
}

// 替代品规则 DTO
export interface SubstituteRuleDTO extends BaseFields {
  ruleCode: string;                  // 规则编码
  ruleName: string;                  // 规则名称
  description: string;               // 规则描述
  priority: number;                  // 优先级
  effectiveStartTime: number;        // 生效开始时间
  effectiveEndTime: number | null;   // 生效结束时间
  matchConfigs: SubstituteMatchConfigDTO[]; // 匹配配置（前端已 parse）
  applyStoreCodes: string[];         // 适用门店编码列表
  applyStoreTypes: string[];         // 适用门店类型列表
  excludeStores: string[];           // 排除门店编码列表
  status: SubstituteRuleStatus;      // 状态
}

// 后端原始返回类型（JSON 字符串字段）
export interface SubstituteRuleRawDTO extends BaseFields {
  ruleCode: string;
  ruleName: string;
  description: string;
  priority: number;
  effectiveStartTime: number;
  effectiveEndTime: number | null;
  matchConfig: string;               // JSON 字符串
  applyStoreCodes: string;           // JSON 字符串
  applyStoreTypes: string;           // JSON 字符串
  excludeStores: string;             // JSON 字符串
  status: SubstituteRuleStatus;
}

// 将后端原始 DTO 转换为前端使用的 DTO
export function parseSubstituteRuleDTO(raw: SubstituteRuleRawDTO): SubstituteRuleDTO {
  const safeParseArray = <T>(jsonStr: string | null | undefined, fallback: T[] = []): T[] => {
    if (!jsonStr) return fallback;
    try {
      const parsed = JSON.parse(jsonStr);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  };

  return {
    ...raw,
    matchConfigs: safeParseArray<SubstituteMatchConfigDTO>(raw.matchConfig),
    applyStoreCodes: safeParseArray<string>(raw.applyStoreCodes),
    applyStoreTypes: safeParseArray<string>(raw.applyStoreTypes),
    excludeStores: safeParseArray<string>(raw.excludeStores),
  };
}

// ============ 请求类型 ============

// 分页查询替代品规则请求
export interface QuerySubstituteRulePageRequest extends PageRequest {
  applyStoreCode?: string;           // 适用门店编码（关联 wms_store_substitution_rule 表查询）
  ruleCode?: string;                 // 规则编码（模糊查询）
  ruleName?: string;                 // 规则名称（模糊查询）
  status?: SubstituteRuleStatus;     // 状态
}

// 新建替代品规则请求
export interface CreateSubstituteRuleRequest {
  ruleName: string;
  description?: string;
  priority: number;
  effectiveStartTime: number;
  effectiveEndTime?: number;
  applyStoreCodes?: string[];          // 适用门店编码列表（By Store Code 模式）
  applyStoreTypes?: string[];          // 适用门店类型列表（By Store Type 模式）
  excludeStores?: string[];            // 排除门店编码列表
  matchConfigs: SubstituteMatchConfigDTO[];
}

// 新建替代品明细请求
export interface CreateSubstituteDetailRequest {
  substituteSku: string;
  priceDifferenceAllowed: boolean;
  maxPriceDifference?: number;
  priority: number;
}

// 编辑替代品规则请求
export interface UpdateSubstituteRuleRequest {
  ruleCode: string;                  // 规则编码（不可修改）
  ruleName?: string;
  description?: string;
  priority?: number;
  effectiveStartTime?: number;
  effectiveEndTime?: number;
  applyStoreCodes?: string[];          // 适用门店编码列表（By Store Code 模式）
  applyStoreTypes?: string[];          // 适用门店类型列表（By Store Type 模式）
  excludeStores?: string[];            // 排除门店编码列表
  matchConfigs?: SubstituteMatchConfigDTO[];
}

// 启用替代品规则请求
export interface EnableSubstituteRuleRequest {
  ruleCode: string;
}

// 禁用替代品规则请求
export interface DisableSubstituteRuleRequest {
  ruleCode: string;
}

// 删除替代品规则请求（单条）
export interface DeleteSubstituteRuleRequest {
  ruleCode: string;                  // 规则编码
}

// 批量删除替代品规则请求
export interface BatchDeleteSubstituteRuleRequest {
  ruleCodes: string[];               // 规则编码列表
}

// 替代品匹配配置项（表单用）
export interface SubstituteMatchConfig {
  id: string;                        // 前端生成的唯一标识
  matchType: MatchType;               // 匹配类型
  matchRule: MatchRule;               // 匹配规则
  priority: string | null;            // 优先级 (L1, L2, L3...)
  categoryLevel?: CategoryLevel;      // 品类层级（仅当 matchType 为 CATEGORY 时使用）
}

// 类别树节点
export interface CategoryTreeNode {
  id: string;
  name: string;
  code: string;
  level: number;                     // 层级：1-一级，2-二级，3-三级
  parentId?: string;
  children?: CategoryTreeNode[];
  itemName?: string;                 // 商品名称（仅三级节点）
  itemPrice?: number;                // 商品价格（仅三级节点）
}

// 替代品明细表单字段（保留兼容）
export interface SubstituteDetailFormItem {
  id: string;                        // 前端生成的唯一标识
  categoryId?: string;               // 类别ID（三级）
  categoryPath?: string[];           // 类别路径 [一级ID, 二级ID, 三级ID]
  substituteSku: string;             // 替换商品SKU（最终选中的商品）
  substituteItemName: string;
  substitutePrice: number;
  priceDifferenceAllowed: boolean;
  maxPriceDifference: number | null;
  priority: number;
}
